import React, { useRef, useState, useEffect } from "react";
import { useColorMode } from "@chakra-ui/react";
import { FiCamera, FiCheck, FiRefreshCw, FiX } from "react-icons/fi";
import { ChakraIcon } from "../ChakraIcon";
import { showToast } from "../../utils/toastUtils";

type CameraCaptureProps = {
  onCapture: (photoDataUrl: string) => void;
  onCancel: () => void;
};

const CameraCapture: React.FC<CameraCaptureProps> = ({ onCapture, onCancel }) => {
  const { colorMode } = useColorMode();
  const isDark = colorMode === "dark";
  const videoRef = useRef<HTMLVideoElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [capturedPhoto, setCapturedPhoto] = useState<string | null>(null);

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({ video: true });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (err) {
      showToast({ title: "Camera access denied or not available.", status: "error" });
      onCancel();
    }
  };

  useEffect(() => {
    startCamera();
    return () => {
      stream?.getTracks().forEach((track) => track.stop());
    };
    // eslint-disable-next-line
  }, []);

  const stopStream = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
  };

  const handleCapture = () => {
    if (!videoRef.current) return;
    const canvas = document.createElement("canvas");
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    const ctx = canvas.getContext("2d");
    if (ctx) {
      ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
      const dataUrl = canvas.toDataURL("image/png");
      stopStream();
      setCapturedPhoto(dataUrl);
    }
  };

  const handleUsePhoto = () => {
    if (capturedPhoto) {
      onCapture(capturedPhoto);
    }
  };

  const handleRetake = () => {
    setCapturedPhoto(null);
    startCamera();
  };

  const handleCancel = () => {
    stopStream();
    onCancel();
  };

  return (
    <div className={`flex flex-col items-center justify-center p-6 rounded-2xl shadow-xl max-w-lg mx-auto animate-fade-in ${isDark ? 'bg-[#1A1C1F] border border-[#2E2F33]' : 'bg-white'}`}>
      {!capturedPhoto ? (
        <>
          <video ref={videoRef} autoPlay playsInline className={`w-full max-w-lg rounded-xl shadow-sm mb-6 border ${isDark ? 'border-[#2E2F33]' : 'border-gray-200'}`} />
          <div className="flex flex-row justify-center gap-4 w-full">
            <button
              onClick={handleCapture}
              disabled={!stream}
              className="flex items-center gap-2 px-6 py-2 rounded-full bg-gradient-to-r from-[#FBD282] to-[#B59143] text-white font-bold shadow-md hover:from-[#E8C16C] hover:to-[#A7843D] transition-all text-base focus:outline-none focus:ring-2 focus:ring-[#B59143] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChakraIcon as={FiCamera} boxSize={5} className="text-white" /> Capture Photo
            </button>
            <button
              onClick={handleCancel}
              className={`flex items-center gap-2 px-6 py-2 rounded-full font-bold shadow-md transition-all text-base focus:outline-none ${isDark ? 'border border-red-500 text-red-400 bg-transparent hover:bg-red-900/20 focus:ring-2 focus:ring-red-700' : 'border-2 border-red-400 text-red-500 bg-white hover:bg-red-50 focus:ring-2 focus:ring-red-200'}`}
            >
              <ChakraIcon as={FiX} boxSize={5} className="text-red-500" /> Cancel
            </button>
          </div>
          {!stream && (
            <p className={`text-xs mt-3 text-center animate-pulse ${isDark ? 'text-[#FBD282]/85' : 'text-[#B59143]'}`}>
              Please allow camera access in your browser to capture your photo...
            </p>
          )}
        </>
      ) : (
        <>
          <img src={capturedPhoto} alt="captured" className={`w-full max-w-lg rounded-xl shadow-sm mb-6 border ${isDark ? 'border-[#2E2F33]' : 'border-gray-200'}`} />
          <div className="flex flex-row justify-center gap-4 w-full">
            <button
              onClick={handleUsePhoto}
              className="flex items-center gap-2 px-6 py-2 rounded-full bg-gradient-to-r from-[#FBD282] to-[#B59143] text-white font-bold shadow-md hover:from-[#E8C16C] hover:to-[#A7843D] transition-all text-base focus:outline-none focus:ring-2 focus:ring-[#B59143]"
            >
              <ChakraIcon as={FiCheck} boxSize={5} className="text-white" /> Use Photo
            </button>
            <button
              onClick={handleRetake}
              className={`flex items-center gap-2 px-6 py-2 rounded-full font-bold shadow-md transition-all text-base focus:outline-none ${isDark ? 'border border-[#E8C16C] text-[#FBD282] bg-transparent hover:bg-[#2A3441] focus:ring-2 focus:ring-[#B59143]' : 'border-2 border-[#E8C16C] text-[#B59143] bg-white hover:bg-[#FFF7E6] focus:ring-2 focus:ring-[#B59143]/40'}`}
            >
              <ChakraIcon as={FiRefreshCw} boxSize={5} className="text-[#B59143]" /> Retake
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default CameraCapture; 